import React, { useMemo } from 'react';
import { TInheritedProps } from '@ts/storybook';
import WrapperDataGrid from '@modules/dataGrid/WrapperDataGrid';
import { GridColDef } from '@mui/x-data-grid-pro';
import {
  IDataGridConfig,
  IAction,
  generateGlobalColumns,
  generateBasicColumn,
  generateDateColumn,
  generateEntityColumn,
  generateSelectColumn,
} from '@ts/storybook';
import { IConfigComponent, IEntity, CEntity, CElement } from '@ts/ui-models';

interface IChildrenProps {
  entity: IEntity;
  refGrid?: React.MutableRefObject<any>;
  isChildren?: boolean;
  isParent?: boolean;
  onClickRemove?: (memberType: 'parent' | 'child', xElementToRemove) => void;
  configComponent?: IConfigComponent;
  inheritedProps?: TInheritedProps<false>;
}

export const defaultConfigComponent = (optionsConfig: any): IConfigComponent => {
  return {
    componentId: `itemsBase${optionsConfig.hierarchy}`,
    aclId: 'tsElement',
    //entity: 'item',
    fetchURL: `/api/v2/item/${optionsConfig.idChildren}/${optionsConfig.hierarchy}`,
    where: optionsConfig.inheritedProps?.isModal ? { isAlive: true } : {},
  };
};

const HierarchyDataGrid = (props: IChildrenProps) => {
  const { isChildren = false, isParent = false } = props;

  const tEntity = new CEntity(props.entity);
  const {
    configComponent = defaultConfigComponent({
      idChildren: tEntity.getID(),
      hierarchy: isParent ? 'parents' : isChildren ? 'children' : 'unknown',
      inheritedProps: props.inheritedProps,
    }),
    inheritedProps,
  } = props;
  const config: IDataGridConfig = {
    columnVisibilityModel: { id: false },
  };

  const extraAction = (): IAction => {
    return {
      id: 'removeMember',
      label: 'Remove',
      icon: 'LinkOff',
      fDisabled: (props, rowSelected) => {
        return Boolean(rowSelected.aliveEnd);
      },
      onClick: handleClick => {
        const entity = new CEntity(handleClick());
        props.onClickRemove?.(isParent ? 'parent' : isChildren ? 'child' : 'child', entity);
      },
    };
  };

  const getColumns = useMemo((): GridColDef<any>[] => {
    const mixProps = { ...props, configComponent };
    const globalCells = generateGlobalColumns(mixProps);
    return [
      globalCells.id(),
      generateEntityColumn(
        { field: isParent ? 'pe' : isChildren ? 'ce' : 'item', headerName: 'Name / ID' },
        { field: isParent ? 'pe' : isChildren ? 'ce' : 'item', mainClass: CElement },
        { inheritedProps }
      ),
      generateBasicColumn({ field: 'relationName', headerName: 'Relation' }, { field: 'relation.name' }),
      generateSelectColumn(
        {
          field: 'isAlive',
          options: [
            { label: 'Established', value: true },
            { label: 'Unestablished', value: false },
          ],
          headerName: 'Status',
        },
        { field: 'isAlive' }
      ),
      generateDateColumn({ field: 'aliveStart', headerName: 'Relation start' }, { field: 'aliveStart' }),
      generateDateColumn({ field: 'aliveEnd', headerName: 'Relation end' }, { field: 'aliveEnd' }),
    ];
  }, [props, configComponent]);

  return (
    <WrapperDataGrid
      ref={props.refGrid}
      title={inheritedProps?.isModal ? null : isParent ? 'Parents' : isChildren ? 'Children' : null}
      configComponent={configComponent}
      dataGridConfig={config}
      columns={getColumns}
      //Extra data
      //extraActions={inheritedProps.isModal ? null : [extraAction()]}
      extraActions={[extraAction()]}
      inheritedProps={inheritedProps}
      withoutDefaultActions
    />
  );
};

export default HierarchyDataGrid;
